
import React, { useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import TalkingPersonAvatar from "@/components/avatar/TalkingPersonAvatar";
import { Upload, Volume2, VolumeX } from "lucide-react";

// Default avatar image
const DEFAULT_AVATAR = "/placeholder.svg";

const PersonTestPage = () => {
  const [isResponding, setIsResponding] = useState(false);
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState<Array<{type: 'user' | 'avatar', text: string}>>([]);
  const [avatarImage, setAvatarImage] = useState(DEFAULT_AVATAR);
  const [input, setInput] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAskQuestion = () => {
    if (!input.trim()) {
      toast({
        title: "Empty Message",
        description: "Please enter a message first.",
        variant: "destructive",
      });
      return;
    }
    
    // Add user message to conversation
    setConversation(prev => [...prev, {type: 'user', text: input}]);
    
    // Add Sentry breadcrumb
    Sentry.addBreadcrumb({
      category: 'avatar',
      message: `User said: ${input}`,
      level: 'info',
    });
    
    // Simulate avatar response
    setIsResponding(true);
    
    // Simulate a delay for the response
    setTimeout(() => {
      const response = `I heard you say: "${input}". This is a simulated response from the talking avatar.`;
      setConversation(prev => [...prev, {type: 'avatar', text: response}]);
      setMessage(response);
      setInput("");
      
      // Speech synthesis is now handled by the avatar component
    }, 500);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setAvatarImage(event.target.result as string);
          toast({
            title: "Image Uploaded",
            description: "Your avatar image has been updated.",
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  // Handle speech end event from the avatar component
  const handleSpeechEnd = () => {
    setIsResponding(false);
  };
  
  // Toggle mute/unmute
  const toggleMute = () => {
    setIsMuted(prev => !prev);
    
    if (!isMuted) {
      window.speechSynthesis.cancel(); // Cancel any ongoing speech
    }
    
    toast({
      title: isMuted ? "Sound Enabled" : "Sound Muted",
      description: isMuted ? "The avatar will now speak out loud." : "The avatar will be silent.",
    });
  };

  // If muted, cancel any speech synthesis
  React.useEffect(() => {
    if (isMuted && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, [isMuted]);

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8 text-center">Talking Person Avatar Demo</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Talking Avatar</CardTitle>
            <CardDescription>
              Upload a photo and watch it "talk" as you interact with it
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Button 
                variant="outline" 
                type="button"
                onClick={triggerFileInput}
                className="flex items-center gap-2"
              >
                <Upload size={16} />
                Upload Photo
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={toggleMute}
                className="flex items-center gap-2"
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                {isMuted ? "Unmute" : "Mute"}
              </Button>
              <input
                ref={fileInputRef}
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
            
            <TalkingPersonAvatar
              imageSrc={avatarImage}
              isSpeaking={isResponding && !isMuted}
              message={message}
              onSpeechEnd={handleSpeechEnd}
            />
            
            <div className="flex gap-2 mt-4">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                onKeyDown={(e) => e.key === "Enter" && handleAskQuestion()}
              />
              <Button 
                onClick={handleAskQuestion}
                disabled={isResponding}
              >
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Conversation</CardTitle>
            <CardDescription>
              Your conversation history with the avatar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4 h-[500px] overflow-y-auto flex flex-col gap-4">
              {conversation.length === 0 ? (
                <div className="text-center text-muted-foreground italic">
                  Your conversation will appear here after you send a message
                </div>
              ) : (
                conversation.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg max-w-[80%] ${
                      msg.type === 'user' 
                        ? 'bg-primary text-primary-foreground self-end' 
                        : 'bg-secondary text-secondary-foreground self-start'
                    }`}
                  >
                    {msg.text}
                  </div>
                ))
              )}
              {isResponding && (
                <div className="bg-secondary text-secondary-foreground p-3 rounded-lg max-w-[80%] self-start animate-pulse">
                  Typing...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PersonTestPage;
